<template>
  <div>
    <slot />
  </div>
</template>

<script>
import axios from 'axios';
import qs from 'qs';

export const API_STATES = {
  success: 'success',
  fetching: 'fetching',
  error: 'error',
};
Object.freeze(API_STATES);

const CancelToken = axios.CancelToken;

axios.interceptors.request.use(config => {
  config.paramsSerializer = params => {
    return qs.stringify(params, {
      arrayFormat: 'indices',
      encode: false,
    });
  };

  return config;
});

export default {
  provide() {
    const context = {};

    Object.defineProperty(context, 'responseHistory', {
      enumerable: true,
      get: () => this.responseHistory,
    });

    Object.defineProperty(context, 'lastResponse', {
      enumerable: true,
      get: () => this.lastResponse,
    });

    Object.defineProperty(context, 'requestParams', {
      enumerable: true,
      get: () => this.requestParams,
    });

    Object.defineProperty(context, 'state', {
      enumerable: true,
      get: () => this.state,
    });

    Object.defineProperty(context, 'url', {
      enumerable: true,
      get: () => this.url,
    });

    return {
      [this.injectContextName]: context,
      setParam: this.setParam,
      sync: this.sync,
      clearHistory: this.clearHistory,
    };
  },
  model: {
    prop: 'value',
    event: 'response',
  },
  props: {
    injectContextName: {
      type: String,
      default: 'context',
    },
    url: {
      type: String,
      required: true,
    },
    params: {
      type: Object,
      default() {
        return {};
      },
    },
    initialResponse: {
      type: Object,
      default() {
        return {};
      },
    },
    value: {
      type: Array,
      default() {
        return [];
      },
    },
    immediate: {
      type: Boolean,
      default: true,
    },
    syncStoreKey: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      state: API_STATES.fetching,
      internalParams: {},
      cancelRequest: undefined,
      responseHistory: [this.initialResponse],
    };
  },
  computed: {
    lastResponse() {
      return this.responseHistory[this.responseHistory.length - 1] || {};
    },
    requestParams() {
      return {
        ...this.params,
        ...this.internalParams,
      };
    },
    requestParamsString() {
      return JSON.stringify(this.requestParams);
    },
    syncStoreContext() {
      if (!this.syncStoreKey) return;

      return {
        url: this.url,
        state: this.state,
        responseHistory: this.responseHistory,
        lastResponse: this.lastResponse,
      };
    },
  },
  watch: {
    url() {
      this.sync();
    },
    syncStoreContext: {
      deep: true,
      handler(syncStoreContext) {
        if (!this.syncStoreKey) return;

        this.$store.dispatch('api/set', {
          key: this.syncStoreKey,
          value: syncStoreContext,
        });
      },
    },
    requestParamsString(before, after) {
      if (before === after) return;
      this.sync();
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.immediate && this.sync();
    });
  },
  methods: {
    setParam(key, value, clearHistory = true) {
      const isChangedValue =
        JSON.stringify(this.internalParams[key]) !== JSON.stringify(value);

      if (!isChangedValue) return;

      clearHistory && this.clearHistory();
      this.$set(this.internalParams, key, value);
    },
    clearHistory() {
      this.responseHistory = [];
    },
    addResponseToHistory(response) {
      this.responseHistory.push(response);
    },
    sync() {
      if (this.cancelRequest) this.cancelRequest();

      this.state = API_STATES.fetching;

      return axios
        .get(this.url, {
          params: this.requestParams,
          cancelToken: new CancelToken(cancel => {
            this.cancelRequest = cancel;
          }),
        })
        .then(response => {
          const responseData = response.data;

          this.state = API_STATES.success;
          this.addResponseToHistory(responseData);
          this.$emit('response', this.responseHistory);
        })
        .catch(error => {
          if (axios.isCancel(error)) {
            return;
          }

          console.error(error);
          this.state = API_STATES.error;
        });
    },
  },
};
</script>
